import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_BASE_URL?.trim() || "";
const AUTH_BASE = "/api/v1/auth";
const IS_DEV = process.env.NODE_ENV !== "production";

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function normalizeUser(u) {
  if (!u) return null;

  const verification = u.verification || {};
  const emailVerification = verification.email || {};
  const mobileVerification = verification.mobile || {};
  const liveImageVerification = verification.liveImage || {};
  const aadhaarVerification = verification.aadhaar || {};
  const subscription = u.subscription || {};
  const userImage = u.userImage || {};

  return {
    id: u.id || u._id || "",
    _id: u._id || u.id || "",

    name: u.name || "",
    email: u.email || "",
    phone: u.phone || "",
    role: u.role || "user",
    slug: u.slug || "",
    isActive: typeof u.isActive === "boolean" ? u.isActive : true,

    dob: u.dob || null,
    gender: u.gender || "other",
    lastSeen: u.lastSeen || null,
    createdAt: u.createdAt || null,
    updatedAt: u.updatedAt || null,

    userImage: {
      url: userImage.url || "",
      uploadedAt: userImage.uploadedAt || null,
    },

    userImageVisibility: u.userImageVisibility || "all",

    verification: {
      email: {
        isVerified: Boolean(emailVerification.isVerified),
        verifiedAt: emailVerification.verifiedAt || null,
      },

      mobile: {
        isVerified: Boolean(mobileVerification.isVerified),
        verifiedAt: mobileVerification.verifiedAt || null,
      },

      liveImage: {
        url: liveImageVerification.url || "",
        isVerified: Boolean(liveImageVerification.isVerified),
        verifiedAt: liveImageVerification.verifiedAt || null,
        verifiedBy: liveImageVerification.verifiedBy || "",
      },

      aadhaar: {
        isVerified: Boolean(aadhaarVerification.isVerified),
        verifiedAt: aadhaarVerification.verifiedAt || null,
        provider: aadhaarVerification.provider || "",
        providerReferenceId: aadhaarVerification.providerReferenceId || "",
        last4: aadhaarVerification.last4 || "",
        verifiedName: aadhaarVerification.verifiedName || "",
        dobMatched: Boolean(aadhaarVerification.dobMatched),
        genderMatched: Boolean(aadhaarVerification.genderMatched),
        failureReason: aadhaarVerification.failureReason || "",
      },

      overallStatus: verification.overallStatus || "incomplete",
    },

    subscription: {
      userType: subscription.userType || "regular",
      hasSubscriptionRecord: Boolean(subscription.hasSubscriptionRecord),
      plan: subscription.plan || "regular",
      paymentStatus: subscription.paymentStatus || null,
      orderStatus: subscription.orderStatus || null,
      amount: Number(subscription.amount || 0),
      currency: subscription.currency || "INR",
      durationDays: subscription.durationDays ?? null,
      startsAt: subscription.startsAt || null,
      expiryDate: subscription.expiryDate || null,
      isExpired: Boolean(subscription.isExpired),
      isPlanActive: Boolean(subscription.isPlanActive),
      canAccessPremium: Boolean(subscription.canAccessPremium),
      raw: subscription.raw || null,
    },

    hasPhone: Boolean(String(u.phone || "").trim()),
    hasUserImage: Boolean(String(userImage.url || "").trim()),
    hasLiveImage: Boolean(String(liveImageVerification.url || "").trim()),
    isEmailVerified: Boolean(emailVerification.isVerified),
    isMobileVerified: Boolean(mobileVerification.isVerified),
    isLiveImageVerified: Boolean(liveImageVerification.isVerified),
    isAadhaarVerified: Boolean(aadhaarVerification.isVerified),

    profileCompletion: {
      hasName: Boolean(String(u.name || "").trim()),
      hasEmail: Boolean(String(u.email || "").trim()),
      hasPhone: Boolean(String(u.phone || "").trim()),
      hasDob: Boolean(u.dob),
      hasGender: Boolean(String(u.gender || "").trim()),

      hasUserImage: Boolean(String(userImage.url || "").trim()),
      hasLiveImage: Boolean(String(liveImageVerification.url || "").trim()),

      emailVerified: Boolean(emailVerification.isVerified),
      mobileVerified: Boolean(mobileVerification.isVerified),
      aadhaarVerified: Boolean(aadhaarVerification.isVerified),
      liveImageVerified: Boolean(liveImageVerification.isVerified),

      userImageVisibility: u.userImageVisibility || "all",
      overallStatus: verification.overallStatus || "incomplete",
      subscriptionActive: Boolean(subscription.isPlanActive),
    },
  };
}

async function apiRequest(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const method = options.method || "GET";
  const body = options.body;
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      ...(!isFormData && body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
    body: body || undefined,
  });

  const text = await response.text().catch(() => "");
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!response.ok) {
    const error = new Error(
      data?.message || `Request failed with status ${response.status}`
    );
    error.status = response.status;
    error.code = data?.code || "";
    error.field = data?.field || "";
    error.data = data;
    throw error;
  }

  return data;
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [rawUser, setRawUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastAuthAction, setLastAuthAction] = useState("init");

  const setAuthUser = useCallback((incomingUser, action = "set-user") => {
    const normalized = normalizeUser(incomingUser || null);
    setRawUser(incomingUser || null);
    setUserState(normalized);
    setIsAuthenticated(Boolean(normalized));
    setLastAuthAction(action);
    return normalized;
  }, []);

  const clearAuthUser = useCallback((action = "clear-user") => {
    setRawUser(null);
    setUserState(null);
    setIsAuthenticated(false);
    setLastAuthAction(action);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const data = await apiRequest(`${AUTH_BASE}/me`, { method: "GET" });
      return setAuthUser(data?.user || null, "fetch-me");
    } catch (err) {
      if (err?.status !== 401) {
        console.error("[AuthContext][me] error:", err);
      }

      clearAuthUser("fetch-me-failed");
      return null;
    }
  }, [setAuthUser, clearAuthUser]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        await fetchMe();
      } finally {
        if (mounted) {
          setLoading(false);
          setAuthReady(true);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [fetchMe]);

  const loginUser = useCallback(
    async ({ identifier, password, remember = false }) => {
      const id = String(identifier || "").trim();
      const pw = String(password || "").trim();

      if (!id || !pw) {
        const err = new Error("Identifier email/phone and password are required");
        err.status = 400;
        throw err;
      }

      const data = await apiRequest(`${AUTH_BASE}/login`, {
        method: "POST",
        body: JSON.stringify({
          identifier: id,
          password: pw,
          remember,
        }),
      });

      if (data?.user) {
        setAuthUser(data.user, "login");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );



  const verifyLoginEmailOtp = useCallback(
    async ({ identifier, password, otp, remember = false }) => {
      const id = String(identifier || "").trim();
      const pw = String(password || "");
      const o = String(otp || "").trim();

      if (!id || !pw || !o) {
        const err = new Error("Identifier, password, and OTP are required");
        err.status = 400;
        throw err;
      }

      const data = await apiRequest(`${AUTH_BASE}/login/verify-email-otp`, {
        method: "POST",
        body: JSON.stringify({
          identifier: id,
          password: pw,
          otp: o,
          remember,
        }),
      });

      if (data?.user) {
        setAuthUser(data.user, "login-email-verified");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const verifyLoginMobileOtp = useCallback(
    async ({ identifier, password, otp, remember = false }) => {
      const id = String(identifier || "").trim();
      const pw = String(password || "");
      const o = String(otp || "").trim();

      if (!id || !pw || !o) {
        const err = new Error("Identifier, password, and OTP are required");
        err.status = 400;
        throw err;
      }

      const data = await apiRequest(`${AUTH_BASE}/login/verify-mobile-otp`, {
        method: "POST",
        body: JSON.stringify({
          identifier: id,
          password: pw,
          otp: o,
          remember,
        }),
      });

      if (data?.user) {
        setAuthUser(data.user, "login-mobile-verified");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const registerUser = useCallback(
    async ({ name, email, phone, password, dob = null, gender = "other" }) => {
      const data = await apiRequest(`${AUTH_BASE}/register`, {
        method: "POST",
        body: JSON.stringify({
          name,
          email,
          phone,
          password,
          dob,
          gender,
        }),
      });

      if (data?.user) {
        setAuthUser(data.user, "register");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest(`${AUTH_BASE}/logout`, { method: "POST" });
    } finally {
      clearAuthUser("logout");
    }
  }, [clearAuthUser]);

  const sendEmailOtp = useCallback(async () => {
    return apiRequest(`${AUTH_BASE}/send-email-otp`, { method: "POST" });
  }, []);

  const verifyEmailOtp = useCallback(
    async ({ otp }) => {
      const data = await apiRequest(`${AUTH_BASE}/verify-email-otp`, {
        method: "POST",
        body: JSON.stringify({ otp }),
      });

      if (data?.user) {
        setAuthUser(data.user, "verify-email");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const sendMobileOtp = useCallback(async ({ phone }) => {
    return apiRequest(`${AUTH_BASE}/send-mobile-otp`, {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
  }, []);

  const verifyMobileOtp = useCallback(
    async ({ otp }) => {
      const data = await apiRequest(`${AUTH_BASE}/verify-mobile-otp`, {
        method: "POST",
        body: JSON.stringify({ otp }),
      });

      if (data?.user) {
        setAuthUser(data.user, "verify-mobile");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const updateBasicUserFields = useCallback(
    async ({ dob, gender }) => {
      const data = await apiRequest(`${AUTH_BASE}/update-basic`, {
        method: "PUT",
        body: JSON.stringify({ dob, gender }),
      });

      if (data?.user) {
        setAuthUser(data.user, "update-basic");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const updateContactInfo = useCallback(
    async ({ email, phone }) => {
      const data = await apiRequest(`${AUTH_BASE}/update-contact`, {
        method: "PUT",
        body: JSON.stringify({ email, phone }),
      });

      if (data?.user) {
        setAuthUser(data.user, "update-contact");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const uploadUserImage = useCallback(
    async ({ imageFile, visibility = "all" }) => {
      if (!imageFile) {
        const err = new Error("Image file is required");
        err.status = 400;
        throw err;
      }

      const fd = new FormData();
      fd.append("image", imageFile);
      fd.append("visibility", visibility);

      const data = await apiRequest(`${AUTH_BASE}/upload-image`, {
        method: "POST",
        body: fd,
      });

      if (data?.user) {
        setAuthUser(data.user, "upload-user-image");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const updateUserImageVisibility = useCallback(
    async ({ visibility }) => {
      const data = await apiRequest(`${AUTH_BASE}/user-image-visibility`, {
        method: "PATCH",
        body: JSON.stringify({ visibility }),
      });

      if (data?.user) {
        setAuthUser(data.user, "update-user-image-visibility");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const uploadLiveImage = useCallback(
    async ({ liveImageFile }) => {
      if (!liveImageFile) {
        const err = new Error("Live image file is required");
        err.status = 400;
        throw err;
      }

      const fd = new FormData();
      fd.append("liveImage", liveImageFile);

      const data = await apiRequest(`${AUTH_BASE}/upload-live-image`, {
        method: "POST",
        body: fd,
      });

      if (data?.user) {
        setAuthUser(data.user, "upload-live-image");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const verifyAadhaar = useCallback(
    async ({ aadhaarNumber }) => {
      const data = await apiRequest(`${AUTH_BASE}/verify-aadhaar`, {
        method: "POST",
        body: JSON.stringify({ aadhaarNumber }),
      });

      if (data?.user) {
        setAuthUser(data.user, "verify-aadhaar");
      } else {
        await fetchMe();
      }

      return data;
    },
    [fetchMe, setAuthUser]
  );

  const requestImageVerification = useCallback(async () => {
    return apiRequest(`${AUTH_BASE}/request-image-verification`, {
      method: "POST",
    });
  }, []);

  const getMySubscription = useCallback(async () => {
    return apiRequest(`${AUTH_BASE}/my-subscription`, {
      method: "GET",
    });
  }, []);

  const forgotPasswordSendOtp = useCallback(async ({ email }) => {
    const em = String(email || "").trim();

    if (!em) {
      const err = new Error("Enter email.");
      err.status = 400;
      throw err;
    }

    if (!isEmail(em)) {
      const err = new Error("Enter a valid email.");
      err.status = 400;
      throw err;
    }

    return apiRequest(`${AUTH_BASE}/forgot-password/send-otp`, {
      method: "POST",
      body: JSON.stringify({ email: em }),
    });
  }, []);

  const resetPasswordWithOtp = useCallback(async ({ email, otp, newPassword }) => {
    const em = String(email || "").trim();
    const o = String(otp || "").trim();
    const np = String(newPassword || "");

    if (!em || !o || !np) {
      const err = new Error("email, otp, and newPassword are required");
      err.status = 400;
      throw err;
    }

    if (!isEmail(em)) {
      const err = new Error("Enter a valid email.");
      err.status = 400;
      throw err;
    }

    if (np.length < 6) {
      const err = new Error("Password must be at least 6 characters");
      err.status = 400;
      throw err;
    }

    return apiRequest(`${AUTH_BASE}/forgot-password/reset`, {
      method: "POST",
      body: JSON.stringify({
        email: em,
        otp: o,
        newPassword: np,
      }),
    });
  }, []);

  const forgotPassword = useCallback(
    async ({ identifier }) => {
      const id = String(identifier || "").trim();

      if (!id) {
        const err = new Error("Enter email.");
        err.status = 400;
        throw err;
      }

      if (!isEmail(id)) {
        const err = new Error("Please enter a valid email for password reset.");
        err.status = 400;
        throw err;
      }

      return forgotPasswordSendOtp({ email: id });
    },
    [forgotPasswordSendOtp]
  );

  const debugAuthValues = useMemo(() => {
    return {
      API_BASE,
      AUTH_BASE,
      loading,
      authReady,
      isAuthenticated,
      lastAuthAction,
      user,
      rawUser,
    };
  }, [
    loading,
    authReady,
    isAuthenticated,
    lastAuthAction,
    user,
    rawUser,
  ]);

  useEffect(() => {
    if (!IS_DEV) return;

    // console.group("[AuthContext Debug]");
    // console.log("API_BASE:", API_BASE);
    // console.log("AUTH_BASE:", AUTH_BASE);
    // console.log("loading:", loading);
    // console.log("authReady:", authReady);
    // console.log("isAuthenticated:", isAuthenticated);
    // console.log("lastAuthAction:", lastAuthAction);
    // console.log("user:", user);
    // console.log("rawUser:", rawUser);
    // console.groupEnd();
  }, [
    loading,
    authReady,
    isAuthenticated,
    lastAuthAction,
    user,
    rawUser,
  ]);

  const value = useMemo(() => {
    return {
      user,
      rawUser,
      loading,
      authReady,
      isAuthenticated,
      lastAuthAction,

      me: fetchMe,
      refresh: fetchMe,
      setUser: setAuthUser,
      clearUser: clearAuthUser,

      loginUser,
      verifyLoginEmailOtp,
      verifyLoginMobileOtp,
      registerUser,
      logout,

      sendEmailOtp,
      verifyEmailOtp,
      sendMobileOtp,
      verifyMobileOtp,

      updateBasicUserFields,
      updateBasic: updateBasicUserFields,
      updateContactInfo,

      uploadUserImage,
      updateUserImageVisibility,
      uploadLiveImage,

      verifyAadhaar,
      requestImageVerification,

      getMySubscription,

      forgotPasswordSendOtp,
      resetPasswordWithOtp,
      forgotPassword,
      resetPassword: resetPasswordWithOtp,

      API_BASE,
      AUTH_BASE,
      debugAuthValues,
    };
  }, [
    user,
    rawUser,
    loading,
    authReady,
    isAuthenticated,
    lastAuthAction,
    fetchMe,
    setAuthUser,
    clearAuthUser,
    loginUser,
    verifyLoginEmailOtp,
    verifyLoginMobileOtp,
    registerUser,
    logout,
    sendEmailOtp,
    verifyEmailOtp,
    sendMobileOtp,
    verifyMobileOtp,
    updateBasicUserFields,
    updateContactInfo,
    uploadUserImage,
    updateUserImageVisibility,
    uploadLiveImage,
    verifyAadhaar,
    requestImageVerification,
    getMySubscription,
    forgotPasswordSendOtp,
    resetPasswordWithOtp,
    forgotPassword,
    debugAuthValues,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}